// src/pages/finance/PaymentReports.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  Tooltip,
  Chip,
  Link,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Stack,
  Avatar
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { es } from 'date-fns/locale';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  RemoveRedEye as ViewIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RotateRight as RotateIcon,
  RestartAlt as ResetIcon,
  Settings as SettingsIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Assignment as AssignmentIcon,
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  MonetizationOn as MonetizationOnIcon,
  Receipt as ReceiptIcon,
  AccountBalanceWallet as WalletIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { format } from 'date-fns';

// Componente Visor Interactivo del Comprobante (Voucher Viewer Pro)
const VoucherViewer = ({ proofImageUrl }) => {
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const handleMouseDown = (e) => {
    e.preventDefault();
    setDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };

  const handleMouseMove = (e) => {
    if (!dragging) return;
    setPan({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handleMouseUp = () => {
    setDragging(false);
  };

  const resetImage = () => {
    setZoom(1);
    setRotation(0);
    setPan({ x: 0, y: 0 });
  };

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        border: '1px solid rgba(226, 232, 240, 0.8)',
        borderRadius: '16px',
        height: 380,
        backgroundColor: '#f8fafc',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        boxShadow: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.02)'
      }}
    >
      {/* Controles Flotantes Glassmorphism */}
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 10,
          display: 'flex',
          gap: 0.5,
          backgroundColor: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(8px)',
          padding: '6px',
          borderRadius: '12px',
          boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.05)',
          border: '1px solid rgba(255,255,255,0.4)'
        }}
      >
        <Tooltip title="Acercar (Zoom +)">
          <IconButton onClick={() => setZoom(z => Math.min(z + 0.25, 4.0))} size="small" color="primary">
            <ZoomInIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Alejar (Zoom -)">
          <IconButton onClick={() => setZoom(z => Math.max(z - 0.25, 0.5))} size="small" color="primary">
            <ZoomOutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Rotar 90°">
          <IconButton onClick={() => setRotation(r => (r + 90) % 360)} size="small" color="primary">
            <RotateIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Restablecer Posición">
          <IconButton onClick={resetImage} size="small" color="primary">
            <ResetIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Imagen Interactiva */}
      <img
        src={proofImageUrl}
        alt="Comprobante de pago"
        style={{
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom}) rotate(${rotation}deg)`,
          transformOrigin: 'center center',
          cursor: dragging ? 'grabbing' : 'grab',
          transition: dragging ? 'none' : 'transform 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain'
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      />
    </Box>
  );
};

// Diálogo de Revisión de Pago Completo
const PaymentReviewDialog = ({ open, onClose, payment, onApprove, onOpenReject }) => {
  const [documentType, setDocumentType] = useState('recibo');
  const [clientDocType, setClientDocType] = useState('DNI');
  const [clientDocNumber, setClientDocNumber] = useState('');
  const [clientBusinessName, setClientBusinessName] = useState('');
  const [mergeInvoices, setMergeInvoices] = useState(false);

  useEffect(() => {
    if (open && payment) {
      setDocumentType(payment.invoice_doc_type || payment.client_profile_doc_type || 'recibo');
      setClientDocType(payment.invoice_client_doc_type || payment.client_profile_doc_type || 'DNI');
      setClientDocNumber(payment.invoice_client_doc_number || payment.client_profile_dni || '');
      setClientBusinessName(payment.invoice_client_business_name || payment.client_profile_business_name || '');
      
      const notes = payment.notes || '';
      const clientRequestedMerge = notes.includes('(Solicita comprobante unificado)');
      setMergeInvoices(clientRequestedMerge);
    }
  }, [open, payment]);

  const isValid = (() => {
    const isMergingOrInvoice = payment?.invoice_id || mergeInvoices;
    if (!isMergingOrInvoice || documentType === 'recibo') return true;
    if (documentType === 'factura') {
      return clientDocNumber.length === 11 && clientBusinessName.trim().length > 0;
    }
    if (documentType === 'boleta') {
      if (clientDocType === 'DNI') return clientDocNumber.length === 8;
      if (clientDocType === 'RUC') return clientDocNumber.length === 11;
      return clientDocNumber.trim().length > 0;
    }
    return true;
  })();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: '24px', p: 1 }
      }}
    >
      <DialogTitle sx={{ fontWeight: 800, pb: 1, color: '#0f172a' }}>Revisar Comprobante de Pago</DialogTitle>
      <DialogContent>
        <Grid container spacing={3} className="mt-1">
          <Grid item xs={12} md={6}>
            <Box className="flex flex-col gap-3">
              {/* Información General de Pago de Forma Compacta */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 1.5,
                p: 2,
                borderRadius: '16px',
                backgroundColor: '#f8fafc',
                border: '1px solid rgba(226, 232, 240, 0.8)'
              }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block' }}>Cliente</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>{payment?.client_name}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block' }}>Recibo Asociado</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>#{payment?.invoice_number}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block' }}>Monto Registrado</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: '#6366f1' }}>S/ {parseFloat(payment?.amount || 0).toFixed(2)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block' }}>Método de Pago</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                    {payment?.payment_method_name} - <span className="font-bold">{payment?.payment_reference || 'S/R'}</span>
                  </Typography>
                </Box>
              </Box>

              {/* Opción de Fusión de Comprobantes para pagos grupales */}
              {payment?.target_invoices && payment.target_invoices.length > 1 && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={mergeInvoices}
                      onChange={(e) => setMergeInvoices(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                      Fusionar en un único comprobante oficial (Boleta/Factura/Recibo)
                    </Typography>
                  }
                  sx={{ mb: 1 }}
                />
              )}

              {/* Panel de Configuración de Comprobante Oficial a Emitir */}
              {(payment?.invoice_id || mergeInvoices) ? (
                <Box sx={{
                  p: 2.5,
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.04) 0%, rgba(147, 51, 234, 0.04) 100%)',
                  border: '1px solid rgba(99, 102, 241, 0.15)',
                  boxShadow: '0 4px 20px rgba(99, 102, 241, 0.05)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#312e81', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SettingsIcon sx={{ color: '#6366f1' }} fontSize="small" />
                    Comprobante Oficial a Emitir al Aprobar
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="dialog-document-type-label">Tipo de Comprobante</InputLabel>
                        <Select
                          labelId="dialog-document-type-label"
                          value={documentType}
                          label="Tipo de Comprobante"
                          onChange={(e) => {
                            const val = e.target.value;
                            setDocumentType(val);
                            if (val === 'factura') {
                              setClientDocType('RUC');
                            } else if (val === 'boleta') {
                              setClientDocType('DNI');
                            }
                          }}
                          sx={{ borderRadius: '10px' }}
                        >
                          <MenuItem value="recibo">Recibo de Servicio Interno</MenuItem>
                          <MenuItem value="boleta">Boleta de Venta Electrónica</MenuItem>
                          <MenuItem value="factura">Factura de Venta Electrónica</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {documentType !== 'recibo' && (
                      <>
                        <Grid item xs={12} sm={4}>
                          <FormControl fullWidth size="small">
                            <InputLabel id="dialog-client-doc-type-label">Doc. Tipo</InputLabel>
                            <Select
                              labelId="dialog-client-doc-type-label"
                              value={clientDocType}
                              label="Doc. Tipo"
                              onChange={(e) => setClientDocType(e.target.value)}
                              disabled={documentType === 'factura'}
                              sx={{ borderRadius: '10px' }}
                            >
                              <MenuItem value="DNI">DNI</MenuItem>
                              <MenuItem value="RUC">RUC</MenuItem>
                              <MenuItem value="CE">CE</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={8}>
                          <TextField
                            fullWidth
                            size="small"
                            label={clientDocType === 'RUC' ? "Número de RUC" : "Número de Documento"}
                            value={clientDocNumber}
                            onChange={(e) => setClientDocNumber(e.target.value.replace(/\D/g, ''))}
                            error={
                              (clientDocType === 'DNI' && clientDocNumber.length > 0 && clientDocNumber.length !== 8) ||
                              (clientDocType === 'RUC' && clientDocNumber.length > 0 && clientDocNumber.length !== 11)
                            }
                            helperText={
                              clientDocType === 'DNI' && clientDocNumber.length > 0 && clientDocNumber.length !== 8
                                ? "Debe tener exactamente 8 dígitos"
                                : clientDocType === 'RUC' && clientDocNumber.length > 0 && clientDocNumber.length !== 11
                                ? "Debe tener exactamente 11 dígitos"
                                : ""
                            }
                            inputProps={{ maxLength: clientDocType === 'RUC' ? 11 : 15 }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                          />
                        </Grid>
                      </>
                    )}

                    {documentType === 'factura' && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Razón Social (Empresa)"
                          value={clientBusinessName}
                          onChange={(e) => setClientBusinessName(e.target.value)}
                          error={clientBusinessName.trim().length === 0}
                          helperText={clientBusinessName.trim().length === 0 ? "La razón social es obligatoria para facturas" : ""}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Box>
              ) : (
                (() => {
                  const notes = payment?.notes || '';
                  const isGroupPayment = notes.includes('Pago agrupado para:') || notes.includes('Pago para factura:');
                  
                  return (
                    <Box sx={{
                      p: 2.5,
                      borderRadius: '16px',
                      bgcolor: isGroupPayment ? 'rgba(147, 51, 234, 0.04)' : 'rgba(46, 125, 50, 0.04)',
                      border: isGroupPayment ? '1px dashed rgba(147, 51, 234, 0.25)' : '1px dashed rgba(46, 125, 50, 0.25)',
                      color: isGroupPayment ? 'secondary.dark' : 'success.dark',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1
                    }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1, color: isGroupPayment ? '#9333ea' : '#2e7d32' }}>
                        {isGroupPayment ? <AssignmentIcon fontSize="small" /> : <ApproveIcon fontSize="small" />}
                        {isGroupPayment ? 'Pago Destinado a Facturas' : 'Abono Directo a Saldo (Pago por Adelantado)'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                        {isGroupPayment ? (
                          <>
                            Este pago reportado es para las facturas: <strong>{notes.replace('Pago agrupado para: ', '').replace('Pago para factura: ', '')}</strong>. Al aprobarlo, el sistema aplicará automáticamente el saldo de <strong>S/ {parseFloat(payment?.amount || 0).toFixed(2)}</strong> para cancelar o amortizar estas facturas pendientes en orden de vencimiento.
                          </>
                        ) : (
                          <>
                            Este reporte de pago no está asociado a ninguna factura vencida. Al aprobarlo, el monto total de <strong>S/ {parseFloat(payment?.amount || 0).toFixed(2)}</strong> se cargará automáticamente al <strong>Saldo a Favor</strong> del cliente y se aplicará en sus próximos recibos.
                          </>
                        )}
                      </Typography>
                    </Box>
                  );
                })()
              )}
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#475569' }}>Comprobante Cargado (Arrastrar/Zoom):</Typography>
            {payment?.proof_image_url ? (
              <VoucherViewer proofImageUrl={payment.proof_image_url} />
            ) : (
              <Box sx={{ height: 380, display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#f8fafc', border: '1px dashed #e2e8f0', borderRadius: '16px' }}>
                <Typography sx={{ color: '#94a3b8', fontWeight: 500 }}>No hay comprobante disponible</Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{ borderRadius: '12px', textTransform: 'none', color: '#64748b', fontWeight: 600 }}
        >
          Cerrar
        </Button>
        {payment?.status === 'pending' && (
          <>
            <Button
              onClick={() => onOpenReject(payment)}
              color="error"
              variant="outlined"
              startIcon={<RejectIcon />}
              sx={{ borderRadius: '12px', textTransform: 'none', px: 3, py: 1, fontWeight: 600 }}
            >
              Rechazar
            </Button>
            <Button
              onClick={() => onApprove(payment.id, {
                document_type: documentType,
                client_document_type: clientDocType,
                client_document_number: clientDocNumber,
                client_business_name: clientBusinessName,
                merge_invoices: mergeInvoices
              })}
              disabled={!isValid}
              color="primary"
              variant="contained"
              startIcon={<ApproveIcon />}
              sx={{ borderRadius: '12px', textTransform: 'none', px: 3, py: 1, fontWeight: 700, backgroundColor: '#6366f1', '&:hover': { backgroundColor: '#4f46e5' } }}
            >
              Aprobar Pago
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

// Diálogo Inteligente de Rechazo con CRUD de Motivos
const RejectionReasonDialog = ({ open, onClose, payment, onConfirmReject, templates, onUpdateTemplates }) => {
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [customReason, setCustomReason] = useState('');
  
  // CRUD panel state
  const [crudOpen, setCrudOpen] = useState(false);
  const [newReason, setNewReason] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [savingTemplates, setSavingTemplates] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedTemplate('');
      setCustomReason('');
      setCrudOpen(false);
    }
  }, [open]);

  const handleSelectTemplate = (val) => {
    setSelectedTemplate(val);
    if (val !== 'custom') {
      setCustomReason(val);
    } else {
      setCustomReason('');
    }
  };

  const handleAddTemplate = async () => {
    if (!newReason.trim()) return;
    const updated = [...templates, newReason.trim()];
    await saveTemplatesToBackend(updated);
    setNewReason('');
  };

  const handleDeleteTemplate = async (index) => {
    const updated = templates.filter((_, i) => i !== index);
    await saveTemplatesToBackend(updated);
  };

  const handleStartEdit = (index, val) => {
    setEditingIndex(index);
    setEditingText(val);
  };

  const handleSaveEdit = async (index) => {
    if (!editingText.trim()) return;
    const updated = [...templates];
    updated[index] = editingText.trim();
    await saveTemplatesToBackend(updated);
    setEditingIndex(null);
  };

  const saveTemplatesToBackend = async (newReasons) => {
    setSavingTemplates(true);
    try {
      const response = await fetch('/api/finance/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          rejection_reasons: newReasons
        })
      });
      if (response.ok) {
        onUpdateTemplates(newReasons);
      }
    } catch (err) {
      console.error('Error saving rejection templates:', err);
    } finally {
      setSavingTemplates(false);
    }
  };

  const handleSubmit = () => {
    onConfirmReject(payment.id, customReason || 'Comprobante no cumple con los requisitos.');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: '24px', p: 1 }
      }}
    >
      <DialogTitle sx={{ fontWeight: 800, pb: 1, color: '#0f172a', display: 'flex', justifyContent: 'between', alignItems: 'center' }}>
        <span>Rechazar Pago</span>
        <IconButton onClick={() => setCrudOpen(!crudOpen)} sx={{ ml: 'auto', color: '#6366f1' }}>
          <SettingsIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        {crudOpen ? (
          // Vista CRUD de Motivos Rápidos
          <Box className="mt-2">
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#334155' }}>
              Gestionar Plantillas de Rechazo
            </Typography>
            <Divider className="mb-3" />
            
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, mb: 3 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Nuevo motivo de rechazo..."
                value={newReason}
                onChange={(e) => setNewReason(e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
              <Button
                variant="contained"
                onClick={handleAddTemplate}
                disabled={savingTemplates}
                startIcon={<AddIcon />}
                sx={{ borderRadius: '10px', textTransform: 'none', backgroundColor: '#6366f1', width: { xs: '100%', sm: 'auto' }, whiteSpace: 'nowrap' }}
              >
                Agregar
              </Button>
            </Box>

            <List sx={{ maxHeight: 250, overflowY: 'auto' }}>
              {templates.map((template, idx) => (
                <ListItem key={idx} divider sx={{ py: 1 }}>
                  {editingIndex === idx ? (
                    <Box className="flex gap-2 w-full mr-12">
                      <TextField
                        fullWidth
                        size="small"
                        value={editingText}
                        onChange={(e) => setEditingText(e.target.value)}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                      />
                      <IconButton onClick={() => handleSaveEdit(idx)} color="success"><SaveIcon /></IconButton>
                      <IconButton onClick={() => setEditingIndex(null)} color="error"><CloseIcon /></IconButton>
                    </Box>
                  ) : (
                    <>
                      <ListItemText primary={template} primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 500 }} />
                      <ListItemSecondaryAction>
                        <IconButton size="small" onClick={() => handleStartEdit(idx, template)} color="primary"><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" onClick={() => handleDeleteTemplate(idx)} color="error"><DeleteIcon fontSize="small" /></IconButton>
                      </ListItemSecondaryAction>
                    </>
                  )}
                </ListItem>
              ))}
            </List>
            
            <Button
              onClick={() => setCrudOpen(false)}
              className="mt-3"
              fullWidth
              variant="outlined"
              sx={{ borderRadius: '10px', textTransform: 'none' }}
            >
              Volver a Rechazar
            </Button>
          </Box>
        ) : (
          // Vista Normal de Rechazo
          <Box className="mt-2 flex flex-col gap-4">
            <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
              Indique el motivo del rechazo del pago de <strong>{payment?.client_name}</strong>. Se le notificará automáticamente por correo electrónico para que pueda corregirlo.
            </Typography>

            <FormControl fullWidth size="small">
              <InputLabel id="rejection-template-label">Motivos Rápidos</InputLabel>
              <Select
                labelId="rejection-template-label"
                value={selectedTemplate}
                label="Motivos Rápidos"
                onChange={(e) => handleSelectTemplate(e.target.value)}
                sx={{ borderRadius: '12px' }}
              >
                {templates.map((template, idx) => (
                  <MenuItem key={idx} value={template}>{template}</MenuItem>
                ))}
                <MenuItem value="custom">-- Escribir motivo personalizado --</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              label="Detalle del Motivo de Rechazo"
              placeholder="Escriba claramente la observación para el cliente..."
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px'
                }
              }}
            />
          </Box>
        )}
      </DialogContent>
      
      {!crudOpen && (
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={onClose}
            sx={{ borderRadius: '12px', textTransform: 'none', color: '#64748b', fontWeight: 600 }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!customReason.trim()}
            color="error"
            variant="contained"
            sx={{ borderRadius: '12px', textTransform: 'none', px: 3, fontWeight: 700 }}
          >
            Confirmar Rechazo
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

const PaymentReports = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  
  // Date and filter states
  const [startDate, setStartDate] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [endDate, setEndDate] = useState(() => new Date());
  const [statusFilter, setStatusFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [paymentMethods, setPaymentMethods] = useState([]);
  
  // Rejection & Settings states
  const [rejectionReasons, setRejectionReasons] = useState([]);
  const [rejectingPayment, setRejectingPayment] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);

  // Snackbar states
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchPayments();
    fetchRejectionReasons();
    fetchPaymentMethods();
  }, [startDate, endDate, statusFilter, methodFilter]);

  const fetchPaymentMethods = async () => {
    try {
      const res = await fetch('/api/payment-methods', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPaymentMethods(data);
      }
    } catch (e) {
      console.error('Error fetching payment methods:', e);
    }
  };

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', format(startDate, 'yyyy-MM-dd'));
      if (endDate) params.append('endDate', format(endDate, 'yyyy-MM-dd'));
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (methodFilter && methodFilter !== 'all') params.append('method', methodFilter);
      if (searchQuery) params.append('search', searchQuery);
      params.append('_t', Date.now());

      const response = await fetch(`/api/payments/list?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();

      const processedData = (Array.isArray(data) ? data : []).map(payment => ({
        ...payment,
        amount: typeof payment.amount === 'string' ? parseFloat(payment.amount) : parseFloat(payment.amount || 0)
      }));

      setPayments(processedData);
    } catch (error) {
      console.error('Error fetching payments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRejectionReasons = async () => {
    try {
      const response = await fetch('/api/finance/settings', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRejectionReasons(data.rejection_reasons || []);
      }
    } catch (err) {
      console.error('Error getting settings rejection_reasons:', err);
    }
  };

  const handleApprove = async (paymentId, billingData = {}) => {
    try {
      const response = await fetch(`/api/payments/${paymentId}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(billingData)
      });

      if (!response.ok) throw new Error('Error al aprobar pago');

      setSnackbar({ open: true, message: 'Pago aprobado con éxito.', severity: 'success' });
      fetchPayments();
      setReviewDialogOpen(false);
      setSelectedPayment(null);
    } catch (error) {
      console.error('Error approving payment:', error);
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    }
  };

  const handleOpenReject = (payment) => {
    setReviewDialogOpen(false);
    setRejectingPayment(payment);
    setRejectDialogOpen(true);
  };

  const handleConfirmReject = async (paymentId, reasonText) => {
    try {
      const response = await fetch(`/api/payments/${paymentId}/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: reasonText })
      });

      if (!response.ok) throw new Error('Error al rechazar pago');

      setSnackbar({ open: true, message: 'Pago rechazado correctamente.', severity: 'warning' });
      fetchPayments();
      setRejectDialogOpen(false);
      setRejectingPayment(null);
      setSelectedPayment(null);
    } catch (error) {
      console.error('Error rejecting payment:', error);
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    }
  };

  const handleExportExcel = () => {
    if (payments.length === 0) {
      alert('No hay datos de pagos para exportar.');
      return;
    }

    const totalCollected = payments.filter(p => (p.status === 'completed' || p.status === 'approved') && p.source !== 'system').reduce((sum, p) => sum + p.amount, 0);

    const headersHtml = `
      <th style="background-color: #6366f1; color: white; padding: 10px;">#</th>
      <th style="background-color: #6366f1; color: white; padding: 10px;">Fecha Pago</th>
      <th style="background-color: #6366f1; color: white; padding: 10px;">Cliente</th>
      <th style="background-color: #6366f1; color: white; padding: 10px;">N° Recibo / Factura</th>
      <th style="background-color: #6366f1; color: white; padding: 10px;">Monto (S/)</th>
      <th style="background-color: #6366f1; color: white; padding: 10px;">Método de Pago</th>
      <th style="background-color: #6366f1; color: white; padding: 10px;">N° Referencia</th>
      <th style="background-color: #6366f1; color: white; padding: 10px;">Origen</th>
      <th style="background-color: #6366f1; color: white; padding: 10px;">Estado</th>
    `;

    const rowsHtml = payments.map((p, idx) => {
      const pDate = p.payment_date ? format(new Date(p.payment_date), 'dd/MM/yyyy HH:mm') : '-';
      let stLabel = p.status === 'completed' || p.status === 'approved' ? 'Completado' : p.status === 'pending' ? 'Pendiente' : p.status === 'voided' ? 'Anulado' : 'Rechazado';
      
      return `
        <tr>
          <td style="border: 1px solid #cbd5e1; padding: 8px;">${idx + 1}</td>
          <td style="border: 1px solid #cbd5e1; padding: 8px;">${pDate}</td>
          <td style="border: 1px solid #cbd5e1; padding: 8px;">${p.client_name || 'N/A'}</td>
          <td style="border: 1px solid #cbd5e1; padding: 8px;">${p.invoice_number || '-'}</td>
          <td style="border: 1px solid #cbd5e1; padding: 8px; text-align: right; font-weight: bold;">${p.amount.toFixed(2)}</td>
          <td style="border: 1px solid #cbd5e1; padding: 8px;">${p.payment_method_name || '-'}</td>
          <td style="border: 1px solid #cbd5e1; padding: 8px;">${p.payment_reference || '-'}</td>
          <td style="border: 1px solid #cbd5e1; padding: 8px;">${(p.source || 'admin').toUpperCase()}</td>
          <td style="border: 1px solid #cbd5e1; padding: 8px;">${stLabel}</td>
        </tr>
      `;
    }).join('');

    const excelTemplate = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <meta charset="utf-8">
        <style>
          body { font-family: Arial, sans-serif; }
          .title { font-size: 16pt; font-weight: bold; color: #1e293b; margin-bottom: 10px; }
          .kpi-table { margin-bottom: 15px; border-collapse: collapse; }
          .kpi-table td { padding: 6px 12px; border: 1px solid #cbd5e1; }
        </style>
      </head>
      <body>
        <div class="title">YinoWISP - Reporte de Pagos Realizados</div>
        <div>Generado el: ${format(new Date(), 'dd/MM/yyyy HH:mm:ss')}</div>
        <br/>
        <table class="kpi-table">
          <tr>
            <td style="background: #e0e7ff; font-weight: bold;">Total Recaudado (Aprobado):</td>
            <td style="background: #e0e7ff; font-weight: bold; text-align: right;">S/ ${totalCollected.toFixed(2)}</td>
          </tr>
          <tr>
            <td style="background: #f1f5f9;">Cantidad de Registros:</td>
            <td style="background: #f1f5f9; text-align: right;">${payments.length}</td>
          </tr>
        </table>
        <br/>
        <table style="border-collapse: collapse; width: 100%;">
          <thead><tr>${headersHtml}</tr></thead>
          <tbody>${rowsHtml}</tbody>
        </table>
      </body>
      </html>
    `;

    const blob = new Blob(['\ufeff' + excelTemplate], { type: 'application/vnd.ms-excel;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `reporte_pagos_${format(new Date(), 'yyyyMMdd_HHmmss')}.xls`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handlePrintPDF = () => {
    if (payments.length === 0) {
      alert('No hay pagos para imprimir.');
      return;
    }

    const printWindow = window.open('', '_blank');
    const totalCollected = payments.filter(p => (p.status === 'completed' || p.status === 'approved') && p.source !== 'system').reduce((sum, p) => sum + p.amount, 0);

    const rowsHtml = payments.map((p, idx) => {
      const pDate = p.payment_date ? format(new Date(p.payment_date), 'dd/MM/yyyy HH:mm') : '-';
      let stLabel = p.status === 'completed' || p.status === 'approved' ? 'Completado' : p.status === 'pending' ? 'Pendiente' : p.status === 'voided' ? 'Anulado' : 'Rechazado';
      let stClass = p.status === 'completed' || p.status === 'approved' ? 'st-ok' : p.status === 'pending' ? 'st-pending' : 'st-void';

      return `
        <tr>
          <td>${idx + 1}</td>
          <td>${pDate}</td>
          <td><strong>${p.client_name || 'N/A'}</strong></td>
          <td>${p.invoice_number || '-'}</td>
          <td style="text-align: right; font-weight: bold;">S/ ${p.amount.toFixed(2)}</td>
          <td>${p.payment_method_name || '-'}</td>
          <td><code>${p.payment_reference || '-'}</code></td>
          <td><span class="st-badge ${stClass}">${stLabel}</span></td>
        </tr>
      `;
    }).join('');

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Reporte de Pagos Realizados - YinoWISP</title>
        <style>
          body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 30px; color: #1e293b; font-size: 11px; }
          .header { display: flex; justify-content: space-between; border-bottom: 2px solid #6366f1; padding-bottom: 15px; margin-bottom: 20px; }
          .title { font-size: 20px; font-weight: 800; color: #1e293b; margin: 0; }
          .subtitle { color: #6366f1; font-size: 12px; font-weight: 600; margin-top: 4px; }
          .kpis { display: flex; gap: 20px; margin-bottom: 20px; }
          .kpi-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 16px; flex: 1; }
          .kpi-val { font-size: 18px; font-weight: 800; color: #6366f1; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th, td { border: 1px solid #e2e8f0; padding: 8px; text-align: left; }
          th { background: #6366f1; color: white; font-weight: 700; }
          .st-badge { padding: 3px 8px; border-radius: 4px; font-weight: 700; font-size: 9px; }
          .st-ok { background: #d1fae5; color: #059669; }
          .st-pending { background: #fef3c7; color: #d97706; }
          .st-void { background: #ffe4e6; color: #e11d48; }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <div class="title">YinoWISP - Reporte de Pagos Realizados</div>
            <div class="subtitle">Control de Recaudación y Cobranzas Reales</div>
          </div>
          <div style="text-align: right;">
            <div><strong>Fecha:</strong> ${format(new Date(), 'dd/MM/yyyy HH:mm')}</div>
            <div><strong>Total Registros:</strong> ${payments.length}</div>
          </div>
        </div>

        <div class="kpis">
          <div class="kpi-card">
            <div style="color: #64748b; font-weight: 600;">Total Recaudado (Aprobado)</div>
            <div class="kpi-val">S/ ${totalCollected.toFixed(2)}</div>
          </div>
          <div class="kpi-card">
            <div style="color: #64748b; font-weight: 600;">Cantidad de Transacciones</div>
            <div class="kpi-val" style="color: #0f172a;">${payments.length}</div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Fecha Pago</th>
              <th>Cliente</th>
              <th>N° Factura/Recibo</th>
              <th style="text-align: right;">Monto</th>
              <th>Método</th>
              <th>Referencia</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml}
          </tbody>
        </table>
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const getStatusChip = (status) => {
    switch (status) {
      case 'completed':
      case 'approved':
        return (
          <Chip
            label="Completado"
            color="success"
            size="small"
            icon={<CheckIcon />}
            sx={{ borderRadius: '8px', fontWeight: 600 }}
          />
        );
      case 'pending':
        return (
          <Chip
            label="Pendiente"
            color="warning"
            size="small"
            sx={{ borderRadius: '8px', fontWeight: 600 }}
          />
        );
      case 'voided':
        return (
          <Chip
            label="Anulado"
            color="default"
            size="small"
            sx={{ borderRadius: '8px', fontWeight: 600, backgroundColor: '#cbd5e1' }}
          />
        );
      case 'rejected':
        return (
          <Chip
            label="Rechazado"
            color="error"
            size="small"
            icon={<CloseIcon />}
            sx={{ borderRadius: '8px', fontWeight: 600 }}
          />
        );
      default:
        return <Chip label={status} size="small" sx={{ borderRadius: '8px' }} />;
    }
  };

  const columns = [
    {
      field: 'payment_date',
      headerName: 'Fecha Pago',
      width: 155,
      valueFormatter: (params) =>
        params.value ? format(new Date(params.value), 'dd/MM/yyyy HH:mm') : ''
    },
    {
      field: 'client_name',
      headerName: 'Cliente',
      width: 210,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
          {params.value || 'Desconocido'}
        </Typography>
      )
    },
    {
      field: 'invoice_number',
      headerName: 'Factura / Destino',
      width: 170,
      renderCell: (params) => {
        if (params.row.target_invoices && params.row.target_invoices.length > 0) {
          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
              {params.row.target_invoices.map((inv, idx) => (
                <Link
                  key={inv.id}
                  href={`/finance/invoices/${inv.id}`}
                  underline="hover"
                  sx={{ fontWeight: 700, color: '#6366f1', display: 'inline-block' }}
                >
                  #{inv.invoice_number}{idx < params.row.target_invoices.length - 1 ? ',' : ''}
                </Link>
              ))}
            </Box>
          );
        }

        if (params.row.invoice_id) {
          return (
            <Link href={`/finance/invoices/${params.row.invoice_id}`} underline="hover" sx={{ fontWeight: 700, color: '#6366f1' }}>
              #{params.value}
            </Link>
          );
        }
        
        const notes = params.row.notes || '';
        if (notes.includes('Pago agrupado para:') || notes.includes('Pago para factura:')) {
          const label = notes.replace('Pago agrupado para: ', '').replace('Pago para factura: ', '');
          return (
            <Tooltip title={notes}>
              <Chip
                label={label}
                color="secondary"
                size="small"
                sx={{ 
                  fontWeight: 'bold', 
                  fontSize: '0.68rem', 
                  borderRadius: '8px', 
                  bgcolor: 'rgba(147, 51, 234, 0.08)', 
                  color: '#9333ea', 
                  border: '1px solid rgba(147, 51, 234, 0.2)',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              />
            </Tooltip>
          );
        }
        
        return (
          <Chip
            label="SALDO ADELANTADO"
            color="success"
            size="small"
            sx={{ fontWeight: 'bold', fontSize: '0.68rem', borderRadius: '8px', bgcolor: 'rgba(46, 125, 50, 0.08)', color: '#2e7d32', border: '1px solid rgba(46, 125, 50, 0.2)' }}
          />
        );
      }
    },
    {
      field: 'amount',
      headerName: 'Monto Recaudado',
      width: 135,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 800, color: '#10b981' }}>
          S/ {parseFloat(params.value || 0).toFixed(2)}
        </Typography>
      )
    },
    {
      field: 'payment_method_name',
      headerName: 'Método',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value || 'N/A'}
          size="small"
          variant="outlined"
          sx={{ borderRadius: '6px', fontWeight: 600 }}
        />
      )
    },
    {
      field: 'payment_reference',
      headerName: 'N° Referencia',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, color: '#475569' }}>
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'source',
      headerName: 'Origen',
      width: 110,
      renderCell: (params) => (
        <Chip
          label={(params.value || 'admin').toUpperCase()}
          size="small"
          sx={{ 
            borderRadius: '6px', 
            fontWeight: 700,
            fontSize: '0.68rem',
            backgroundColor: params.value === 'portal' ? 'rgba(99, 102, 241, 0.1)' : 'rgba(100, 116, 139, 0.1)',
            color: params.value === 'portal' ? '#6366f1' : '#475569'
          }}
        />
      )
    },
    {
      field: 'status',
      headerName: 'Estado',
      width: 120,
      renderCell: (params) => getStatusChip(params.value)
    },
    {
      field: 'actions',
      headerName: 'Acciones Auditables',
      width: 160,
      renderCell: (params) => (
        <Box className="flex gap-1">
          <Tooltip title="Ver y Auditar Comprobante">
            <IconButton
              size="small"
              onClick={() => {
                setSelectedPayment(params.row);
                setReviewDialogOpen(true);
              }}
              sx={{ color: '#6366f1', backgroundColor: '#6366f110', '&:hover': { backgroundColor: '#6366f120' } }}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row.status === 'pending' && (
            <>
              <Tooltip title="Aprobar Pago">
                <IconButton
                  size="small"
                  color="success"
                  onClick={() => handleApprove(params.row.id)}
                  sx={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', '&:hover': { backgroundColor: 'rgba(16, 185, 129, 0.2)' } }}
                >
                  <ApproveIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Rechazar con Motivo">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleOpenReject(params.row)}
                  sx={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.2)' } }}
                >
                  <RejectIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      )
    }
  ];

  // Cálculos de Resumen KPI
  const completedPayments = payments.filter(p => p.status === 'completed' || p.status === 'approved');
  // Excluir aplicaciones internas de Saldo a Favor / sistema (source === 'system') para no duplicar ingresos de saldo prepagado
  const totalCollected = completedPayments.filter(p => p.source !== 'system').reduce((acc, p) => acc + p.amount, 0);
  const countCompleted = completedPayments.length;
  const countPending = payments.filter(p => p.status === 'pending').length;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Cabecera Principal */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, mb: 3, gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px', fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
            Reporte e Historial de Pagos Realizados
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5, fontWeight: 500 }}>
            Consulte los pagos recibidos según su <strong>Fecha de Pago</strong> real, audite comprobantes y genere reportes oficiales.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            startIcon={<PdfIcon />}
            onClick={handlePrintPDF}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 700,
              color: '#6366f1',
              borderColor: '#cbd5e1',
              backgroundColor: 'white',
              '&:hover': { borderColor: '#6366f1', backgroundColor: 'rgba(99, 102, 241, 0.04)' }
            }}
          >
            Imprimir Reporte (PDF)
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportExcel}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 700,
              backgroundColor: '#10b981',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#059669' }
            }}
          >
            Exportar Excel (.xls)
          </Button>
        </Stack>
      </Box>

      {/* Tarjetas resumen KPI */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', border: '1px solid #e2e8f0', backgroundColor: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>TOTAL RECAUDADO (FECHAS SELECCIONADAS)</Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#10b981', mt: 0.5 }}>S/ {totalCollected.toFixed(2)}</Typography>
            </Box>
            <Avatar sx={{ bgcolor: 'rgba(16, 185, 129, 0.1)', color: '#10b981', width: 44, height: 44 }}>
              <MonetizationOnIcon />
            </Avatar>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', border: '1px solid #e2e8f0', backgroundColor: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>PAGOS APROBADOS</Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#6366f1', mt: 0.5 }}>{countCompleted} registros</Typography>
            </Box>
            <Avatar sx={{ bgcolor: 'rgba(99, 102, 241, 0.1)', color: '#6366f1', width: 44, height: 44 }}>
              <ReceiptIcon />
            </Avatar>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', border: '1px solid #e2e8f0', backgroundColor: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>PAGOS PENDIENTES DE REVISIÓN</Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#f59e0b', mt: 0.5 }}>{countPending} por auditar</Typography>
            </Box>
            <Avatar sx={{ bgcolor: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', width: 44, height: 44 }}>
              <WalletIcon />
            </Avatar>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', border: '1px solid #e2e8f0', backgroundColor: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>TOTAL REGISTROS FILTRADOS</Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', mt: 0.5 }}>{payments.length}</Typography>
            </Box>
            <Avatar sx={{ bgcolor: 'rgba(15, 23, 42, 0.08)', color: '#0f172a', width: 44, height: 44 }}>
              <FilterIcon />
            </Avatar>
          </Paper>
        </Grid>
      </Grid>

      {/* Barra de Filtros Avanzados por Fecha de Pago */}
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: '20px', border: '1px solid #e2e8f0', backgroundColor: 'white', mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns} locale={es}>
              <DatePicker
                label="Fecha Pago Inicio"
                value={startDate}
                onChange={(date) => setStartDate(date)}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    sx: { '& .MuiOutlinedInput-root': { borderRadius: '10px' } }
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns} locale={es}>
              <DatePicker
                label="Fecha Pago Fin"
                value={endDate}
                onChange={(date) => setEndDate(date)}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    sx: { '& .MuiOutlinedInput-root': { borderRadius: '10px' } }
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Estado de Pago</InputLabel>
              <Select
                value={statusFilter}
                label="Estado de Pago"
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{ borderRadius: '10px' }}
              >
                <MenuItem value="all">Todos los Estados</MenuItem>
                <MenuItem value="completed">Completados / Aprobados</MenuItem>
                <MenuItem value="pending">Pendientes de Revisión</MenuItem>
                <MenuItem value="voided">Anulados</MenuItem>
                <MenuItem value="rejected">Rechazados</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Método de Pago</InputLabel>
              <Select
                value={methodFilter}
                label="Método de Pago"
                onChange={(e) => setMethodFilter(e.target.value)}
                sx={{ borderRadius: '10px' }}
              >
                <MenuItem value="all">Todos los Métodos</MenuItem>
                {paymentMethods.map(pm => (
                  <MenuItem key={pm.id} value={pm.id}>{pm.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={fetchPayments}
              startIcon={<RefreshIcon />}
              sx={{ borderRadius: '10px', height: '40px', textTransform: 'none', fontWeight: 600, color: '#475569', borderColor: '#cbd5e1' }}
            >
              Actualizar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* DataGrid de Pagos Realizados */}
      <Paper
        sx={{
          borderRadius: '20px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
          border: '1px solid rgba(0,0,0,0.05)',
          overflow: 'hidden',
          backgroundColor: 'white'
        }}
      >
        <DataGrid
          rows={payments}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          loading={loading}
          autoHeight
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8fafc',
              borderBottom: '1px solid rgba(0,0,0,0.06)'
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid rgba(0,0,0,0.04)'
            }
          }}
        />
      </Paper>

      {/* MODAL DETALLES DEL COMPROBANTE */}
      {selectedPayment && (
        <PaymentReviewDialog
          open={reviewDialogOpen}
          onClose={() => {
            setReviewDialogOpen(false);
            setSelectedPayment(null);
          }}
          payment={selectedPayment}
          onApprove={handleApprove}
          onOpenReject={handleOpenReject}
        />
      )}

      {/* MODAL INTELIGENTE DE MOTIVO DE RECHAZO */}
      {rejectingPayment && (
        <RejectionReasonDialog
          open={rejectDialogOpen}
          onClose={() => {
            setRejectDialogOpen(false);
            setRejectingPayment(null);
          }}
          payment={rejectingPayment}
          onConfirmReject={handleConfirmReject}
          templates={rejectionReasons}
          onUpdateTemplates={(updated) => setRejectionReasons(updated)}
        />
      )}

      {/* SNACKBAR DE AVISOS */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ borderRadius: '12px', boxShadow: '0 8px 16px rgba(0,0,0,0.1)' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PaymentReports;